// Placeholder interfaces based on backend entities
// Ideally, these would be shared types from a monorepo or generated from OpenAPI spec

export enum QuestionDataKeyFrontend {
    FIRST_NAME = 'firstName',
    MIDDLE_NAME = 'middleName',
    LAST_NAME = 'lastName',
    DATE_OF_BIRTH = 'dateOfBirth',
    PHONE_NUMBER = 'phoneNumber',
    EMAIL = 'emailAddress',
    NATIONALITY = 'nationality',
    ORIGIN_COUNTRY = 'originCountry',
    ORIGIN_CITY = 'originCity',
    WILLING_TO_WORK_IN_NETHERLANDS = 'willingToWorkInNetherlands',
    HOUSING_NEEDED = 'housingNeeded',
    OWN_TRANSPORT = 'ownTransport',
    DESIRED_COMPENSATION_BASIS = 'desiredCompensationBasis',
    DESIRED_COMPENSATION_AMOUNT = 'desiredCompensationAmount',
    DRIVING_LICENSE_B = 'drivingLicenseB',
    INTERVIEW_AVAILABILITY = 'interviewAvailability',
    ZZP_STATUS = 'zzp',
    LANGUAGE = 'language',
    PROFESSION = 'professionKey',
    CUSTOM_OCCUPATION = 'customOccupation',
}

export enum QuestionTypeFrontend {
    LANGUAGE_SELECTION = "LANGUAGE_SELECTION",
    PROFESSION_SELECTION = "PROFESSION_SELECTION",
    CUSTOM_OCCUPATION_INPUT = "CUSTOM_OCCUPATION_INPUT",
    PROFESSION = 'PROFESSION',
    // On the bot side, FINAL_PROFESSION_CONFIRMATION is the "any other
    // professions?" gate between the profession block and personal/cert
    // collection. The bot routes the worker's reply through its
    // ADD_PROFESSION / NEGATIVE / KNOWLEDGE_QUESTION classifier. On the
    // public-share form the worker just types in the field — no classifier;
    // the answer lives as plain text. Until a richer multi-profession UI
    // ships, we render it as a regular TEXT row.
    FINAL_PROFESSION_CONFIRMATION = 'FINAL_PROFESSION_CONFIRMATION',
    PERSONAL = 'PERSONAL',
    CV_UPLOAD = 'CV_UPLOAD',
    ACTION = "ACTION",
    CLOSING = "CLOSING",
}

export enum AnswerTypeFrontend {
    BOOLEAN = 'BOOLEAN',
    DATE = 'DATE',
    RADIO = 'RADIO',
    MULTISELECT = 'MULTISELECT',
    FILE = 'FILE',
    TEXT = 'TEXT',
    TEXT_AREA = 'TEXT_AREA',
    NUMBER = 'NUMBER',
    LIST_OBJECT = 'LIST_OBJECT'
}

export interface QuestionOptionDtoFrontend {
    key: string;
    label: string;
    answerType: AnswerTypeFrontend;
    options?: QuestionOptionDtoFrontend[];
    answer?: string | any[];
    answeredAt?: Date;
    questionShorten?: string;
    isRequiredAnswer: boolean;
    lastAnsweredByActorType?: LogActorTypeFrontend | null;
    lastAnsweredByActorId?: string | null;
    listItemSchema?: ListItemFieldDefinitionFrontend[];
    createdAt: Date;
    updatedAt: Date;
}

// Validation rules for list item fields
export interface FieldValidationRulesFrontend {
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    pattern?: string;
    errorMessage?: string;
    dateFormat?: string;
}

// Conditional field display
export interface FieldConditionFrontend {
    dependsOn: string;
    operator: 'equals' | 'notEquals' | 'contains' | 'greaterThan' | 'lessThan' | 'in' | 'notIn';
    value: any;
}

export interface ListItemFieldDefinitionFrontend {
    fieldName: string;
    label: string;
    fieldType: AnswerTypeFrontend;
    isRequired?: boolean;
    options?: QuestionOptionDtoFrontend[];
    defaultValue?: any;
    validationRules?: FieldValidationRulesFrontend;
    displayCondition?: FieldConditionFrontend;
    placeholder?: string;
    helpText?: string;
}

export interface SessionQuestionEntityFrontend {
    id: number;
    sessionId: number;
    workerId: string;
    questionId: number;
    question: string;
    questionName?: string;
    questionKey?: string | null;
    questionDataKey?: QuestionDataKeyFrontend | null;
    questionType: QuestionTypeFrontend;
    answerType: AnswerTypeFrontend;
    options?: QuestionOptionDtoFrontend[];
    // `answer` removed per worker-as-truth redesign — derive via getAnswerForQuestion(q, worker).
    answeredAt?: Date;
    questionShorten?: string;
    isRequiredAnswer: boolean;
    lastAnsweredByActorType?: LogActorTypeFrontend | null;
    lastAnsweredByActorId?: string | null;
    listItemSchema?: ListItemFieldDefinitionFrontend[];
    minItems?: number;
    maxItems?: number;
    requiresVerification?: boolean;
    showInCV?: boolean;
    consentCopyVersion?: string;
    privacyPolicyVersion?: string;
    privacyPolicyUrl?: string;
    consentAcceptedCopy?: string;
    consentDeclinedCopy?: string;
    consentCopyPendingReview?: boolean;
    /**
     * True when the answer was populated by AI extraction and is awaiting
     * worker confirmation. Set by the WhatsApp CV-prefill path and by the
     * public-form `/ai-prefill/apply` endpoint; cleared by a worker-driven
     * write (bulk-update on the public form, or the chat-flow boundary).
     */
    cvPrefilled?: boolean;
    createdAt: Date;
    updatedAt: Date;
}

/**
 * Minimal Worker DTO shape returned alongside session_questions by
 * `GET /sessions/worker/:workerId/questions`. Only fields the CV editor and
 * related UIs actually consume — other worker fields (status, recruiter, etc.)
 * are kept loose via index signature for forwards compatibility.
 */
export interface WorkerDtoFrontend {
    id: number;
    workerId: string;
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
    prefix?: string | null;
    emailAddress?: string | null;
    phoneNumber?: string | null;
    dateOfBirth?: string | null;
    nationality?: string | null;
    homeAddress?: {
        street?: string | null;
        houseNumber?: string | null;
        postalCode?: string | null;
        city?: string | null;
        country?: string | null;
    } | null;
    originCountry?: string | null;
    originCity?: string | null;
    willingToWorkInNetherlands?: boolean | null;
    housingNeeded?: boolean | null;
    ownTransport?: boolean | null;
    desiredCompensationBasis?: 'GROSS_HOURLY' | 'GROSS_MONTHLY' | 'ZZP_HOURLY' | null;
    desiredCompensationAmount?: number | null;
    zzp?: boolean | null;
    languages?: string[] | null;
    professionKey?: string | null;
    customOccupation?: string | null;
    isCustomOccupation?: boolean;
    drivingLicenseB?: boolean | null;
    interviewAvailability?: string | null;
    workExperiences?: any[] | null;
    professionalCertifications?: any[] | null;
    education?: any[] | null;
    skills?: string[] | null;
    customAnswers?: Record<string, any> | null;
    /**
     * Multi-profession set from `worker_professions`. Backend exposes this
     * on WorkerDto so the public-share form can filter the
     * "Other Professions?" dropdown to exclude professions the worker
     * already has on file.
     */
    professions?: Array<{
        professionKey: string;
        isActive: boolean;
        isPrimary: boolean;
        addedVia: string;
        customOccupation?: string | null;
    }> | null;
    [key: string]: any;
}

export interface WorkerWithSessionQuestionsResponse {
    worker: WorkerDtoFrontend;
    questions: SessionQuestionEntityFrontend[];
}

/**
 * Frontend mirror of the backend WORKER_ANSWER_MAP. Maps a QuestionDataKey to a
 * projector that reads the corresponding worker field. Mirrors
 * `optima-backend/src/modules/answer-dispatch/worker-answer-map.ts` — keep them in sync.
 */
const FRONTEND_ANSWER_MAP: Record<QuestionDataKeyFrontend, (w: WorkerDtoFrontend) => unknown> = {
    [QuestionDataKeyFrontend.FIRST_NAME]: (w) => w.firstName ?? null,
    [QuestionDataKeyFrontend.MIDDLE_NAME]: (w) => w.middleName ?? null,
    [QuestionDataKeyFrontend.LAST_NAME]: (w) => w.lastName ?? null,
    [QuestionDataKeyFrontend.EMAIL]: (w) => w.emailAddress ?? null,
    [QuestionDataKeyFrontend.PHONE_NUMBER]: (w) => w.phoneNumber ?? null,
    [QuestionDataKeyFrontend.DATE_OF_BIRTH]: (w) => {
        if (!w.dateOfBirth) return null;
        const d = new Date(w.dateOfBirth);
        return isNaN(d.getTime()) ? String(w.dateOfBirth) : d.toISOString().slice(0, 10);
    },
    [QuestionDataKeyFrontend.NATIONALITY]: (w) => w.nationality ?? null,
    [QuestionDataKeyFrontend.ORIGIN_COUNTRY]: (w) =>
        w.originCountry ?? w.homeAddress?.country ?? null,
    [QuestionDataKeyFrontend.ORIGIN_CITY]: (w) =>
        w.originCity ?? w.homeAddress?.city ?? null,
    [QuestionDataKeyFrontend.WILLING_TO_WORK_IN_NETHERLANDS]: (w) =>
        w.willingToWorkInNetherlands == null ? null : w.willingToWorkInNetherlands ? 'true' : 'false',
    [QuestionDataKeyFrontend.HOUSING_NEEDED]: (w) =>
        w.housingNeeded == null ? null : w.housingNeeded ? 'true' : 'false',
    [QuestionDataKeyFrontend.OWN_TRANSPORT]: (w) =>
        w.ownTransport == null ? null : w.ownTransport ? 'true' : 'false',
    [QuestionDataKeyFrontend.DESIRED_COMPENSATION_BASIS]: (w) => w.desiredCompensationBasis ?? null,
    [QuestionDataKeyFrontend.DESIRED_COMPENSATION_AMOUNT]: (w) => w.desiredCompensationAmount ?? null,
    [QuestionDataKeyFrontend.ZZP_STATUS]: (w) => (w.zzp == null ? null : w.zzp ? 'true' : 'false'),
    [QuestionDataKeyFrontend.LANGUAGE]: (w) => (Array.isArray(w.languages) && w.languages.length > 0 ? w.languages[0] : null),
    [QuestionDataKeyFrontend.PROFESSION]: (w) => w.professionKey ?? null,
    [QuestionDataKeyFrontend.CUSTOM_OCCUPATION]: (w) => w.customOccupation ?? null,
    [QuestionDataKeyFrontend.DRIVING_LICENSE_B]: (w) => (w.drivingLicenseB == null ? null : w.drivingLicenseB ? 'true' : 'false'),
    [QuestionDataKeyFrontend.INTERVIEW_AVAILABILITY]: (w) => w.interviewAvailability ?? null,
};

/**
 * Project the current answer value for a question from the worker entity.
 * Returns `null` when the worker is missing or the field is empty.
 *
 * For LIST_OBJECT-shaped questions the backend tags them with WORK_EXPERIENCES /
 * PROFESSIONAL_CERTIFICATIONS / EDUCATION / SKILLS dataKeys (added in the worker-as-truth
 * redesign). Those map to typed JSONB columns on worker via the keys below — we read
 * them as arrays. Questions without a dataKey fall back to `worker.customAnswers[questionKey]`.
 */
export function getAnswerForQuestion(
    question: Pick<SessionQuestionEntityFrontend, 'questionDataKey' | 'questionKey'> | null | undefined,
    worker: WorkerDtoFrontend | null | undefined,
): unknown {
    if (!worker || !question) return null;
    const dataKey = question.questionDataKey;
    if (dataKey) {
        const mapped = FRONTEND_ANSWER_MAP[dataKey];
        if (mapped) return mapped(worker);
        // Array-typed dataKeys land on direct jsonb columns
        switch (dataKey as string) {
            case 'workExperiences': return worker.workExperiences ?? null;
            case 'professionalCertifications': return worker.professionalCertifications ?? null;
            case 'education': return worker.education ?? null;
            case 'skills': return worker.skills ?? null;
        }
    }
    // No dataKey → customAnswers fallback
    const key = question.questionKey;
    if (key && worker.customAnswers) {
        return worker.customAnswers[key] ?? null;
    }
    return null;
}

export enum LogActorTypeFrontend {
    WORKER = 'WORKER',
    USER = 'USER',
    AI = 'AI',
    SYSTEM = 'SYSTEM',
}

export enum LogActionFrontend {
    CREATED = 'CREATED',
    ANSWERED = 'ANSWERED',
    UPDATED = 'UPDATED',
}

export enum CommunicationChannelFrontend {
    WHATSAPP = 'WHATSAPP',
    AI_BOT = 'AI_BOT'
}

export interface SessionQuestionLogEntityFrontend {
    id: number;
    timestamp: string; // Date as string
    actorType: LogActorTypeFrontend;
    actorId?: string | null;
    action: LogActionFrontend;
    sessionQuestionId: number;
    sessionId: number;
    workerId: string;
    oldValue?: Record<string, any> | null;
    newValue?: Record<string, any> | null;
    details?: string | null;
}

import axiosInstance from "./axiosInstance"; // Use axiosInstance
import logger from "../utils/logger"; // For logging errors

// DTO for the request body when an admin updates an answer
export interface AdminUpdateAnswerPayload {
    answer: string;
}

/**
 * Fetches the worker entity + session_question metadata for a worker.
 *
 * Backend endpoint shape changed in PR1 (worker-as-truth): the response is now
 * `{ worker, questions }` where `questions` carry metadata only and the answer
 * values live on `worker`. Use `getAnswerForQuestion(question, worker)` to
 * derive the displayed answer for any given question.
 */
export const fetchWorkerWithSessionQuestions = async (
    workerId: string,
): Promise<WorkerWithSessionQuestionsResponse | null> => {
    try {
        logger.debug(`Fetching worker + session questions for worker ${workerId}`);
        const response = await axiosInstance.get<WorkerWithSessionQuestionsResponse>(
            `/sessions/worker/${workerId}/questions`,
        );
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 404 || error.response?.status === 500) {
            logger.debug(`No worker + session questions found for ${workerId}`);
            return null;
        }
        logger.error(`Failed to fetch worker + session questions for ${workerId}: ${error.message}`, error);
        throw new Error(`Failed to fetch questions for worker ${workerId}. Please try again or contact administration.`);
    }
};

/**
 * Compatibility wrapper: returns only the session_question array from the new
 * `{ worker, questions }` response. Existing callers that only render question
 * metadata keep working unchanged; new code should prefer
 * `fetchWorkerWithSessionQuestions` so it can derive answers from the worker.
 */
export const fetchSessionQuestionsForWorker = async (
    workerId: string,
): Promise<SessionQuestionEntityFrontend[]> => {
    const wrapped = await fetchWorkerWithSessionQuestions(workerId);
    return wrapped?.questions ?? [];
};

/**
 * Fetches the history of changes for a specific session question.
 * @param sessionQuestionId The ID of the session question.
 * @returns Promise<SessionQuestionLogEntityFrontend[]>
 */
export const fetchSessionQuestionHistory = async (sessionQuestionId: number): Promise<SessionQuestionLogEntityFrontend[]> => {
    try {
        logger.debug(`Fetching history for session question ${sessionQuestionId}`);
        const response = await axiosInstance.get<SessionQuestionLogEntityFrontend[]>(`/sessions/questions/${sessionQuestionId}/history`);
        return response.data;
    } catch (error: any) {
        logger.error(`Failed to fetch history for session question ${sessionQuestionId}: ${error.message}`, error);
        throw new Error(`Failed to fetch history for session question ${sessionQuestionId}. Please try again or contact administration.`);
    }
};

/**
 * Allows an admin to update the answer for a specific session question.
 * @param sessionQuestionId The ID of the session question to update.
 * @param payload The new answer.
 * @returns Promise<SessionQuestionEntityFrontend>
 */
export const updateSessionQuestionAnswerByAdmin = async (
    sessionQuestionId: number, 
    payload: AdminUpdateAnswerPayload
): Promise<SessionQuestionEntityFrontend> => {
    try {
        logger.debug(`Updating answer for session question ${sessionQuestionId}`);
        const response = await axiosInstance.put<SessionQuestionEntityFrontend>(
            `/sessions/questions/${sessionQuestionId}/admin-answer`, 
            payload
        );
        return response.data;
    } catch (error: any) {
        logger.error(`Failed to update answer for session question ${sessionQuestionId}: ${error.message}`, error);
        throw new Error(`Failed to update answer for session question ${sessionQuestionId}. Please try again or contact administration.`);
    }
};

export interface BulkUpdateAnswerPayloadItem {
    sessionQuestionId: number;
    answer: string;
}

export const bulkUpdateSessionQuestionAnswers = async (workerId: string, updates: BulkUpdateAnswerPayloadItem[]): Promise<SessionQuestionEntityFrontend[]> => {
    try {
        const response = await axiosInstance.post<SessionQuestionEntityFrontend[]>(`/sessions/worker/${workerId}/bulk-update-session-question-answers`, updates);
        return response.data; // Assuming the backend returns the updated list of questions for the worker
    } catch (error: any) {
        logger.error(`Failed to bulk update session question answers for worker ${workerId}: ${error.message}`, error);
        throw new Error(`Failed to bulk update answers for worker ${workerId}.`);
    }
};

/**
 * Updates the showInCV flag for a specific session question.
 * @param sessionQuestionId The ID of the session question to update.
 * @param showInCV Whether to show this question in the CV.
 * @returns Promise<SessionQuestionEntityFrontend>
 */
export const updateSessionQuestionShowInCV = async (
    sessionQuestionId: number,
    showInCV: boolean
): Promise<SessionQuestionEntityFrontend> => {
    try {
        logger.debug(`Updating showInCV for session question ${sessionQuestionId} to ${showInCV}`);
        const response = await axiosInstance.patch<SessionQuestionEntityFrontend>(
            `/sessions/questions/${sessionQuestionId}/show-in-cv`,
            { showInCV }
        );
        return response.data;
    } catch (error: any) {
        logger.error(`Failed to update showInCV for session question ${sessionQuestionId}: ${error.message}`, error);
        throw new Error(`Failed to update showInCV for session question ${sessionQuestionId}.`);
    }
}; 
